import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { t } from "../../lib/i18n";
import { toast } from "sonner";
import { Modal, SettingsField } from "./_shared";

// Iter 28 — Admin CRUD for people credited on the /creators page.
// Fields mirror the Creator model: name/role/bio/avatar_url + socials.
// display_order controls the sort on the public page (ascending).
const EMPTY = {
  id: null,
  name: "",
  role: "",
  bio: "",
  avatar_url: "",
  discord_url: "",
  twitter_url: "",
  youtube_url: "",
  website_url: "",
  display_order: 0,
};

export default function CreatorsAdmin() {
  const [creators, setCreators] = useState([]);
  const [modal, setModal] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const r = await api.get("/creators");
    setCreators(r.data);
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!modal.name.trim()) { toast.error(t.nameRequired); return; }
    setBusy(true);
    try {
      const payload = {
        name: modal.name.trim(),
        role: modal.role || null,
        bio: modal.bio || "",
        avatar_url: modal.avatar_url || null,
        discord_url: modal.discord_url || null,
        twitter_url: modal.twitter_url || null,
        youtube_url: modal.youtube_url || null,
        website_url: modal.website_url || null,
        display_order: Number(modal.display_order) || 0,
      };
      if (modal.id) {
        await api.put(`/admin/creators/${modal.id}`, payload);
      } else {
        await api.post("/admin/creators", payload);
      }
      toast.success(t.saved);
      setModal(null);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || t.failed);
    } finally { setBusy(false); }
  };

  const del = async (id) => {
    if (!window.confirm(t.confirmDelete)) return;
    try {
      await api.delete(`/admin/creators/${id}`);
      toast.success(t.deleted);
      await load();
    } catch { toast.error(t.failed); }
  };

  return (
    <div data-testid="creators-admin" className="mt-8 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-display uppercase italic text-2xl">{t.tabCreators}</h2>
          <p className="text-xs text-white/50 font-mono mt-1">{t.creatorsAdminHint}</p>
        </div>
        <button
          onClick={() => setModal({ ...EMPTY })}
          data-testid="creator-new-btn"
          className="px-4 py-2 border border-[#FF0055] text-[#FF0055] hover:bg-[#FF0055]/10 text-xs uppercase tracking-[0.25em] font-bold"
        >
          + {t.newCreator}
        </button>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
        {creators.length === 0 && (
          <div className="text-white/40 font-mono text-sm">{t.noCreatorsYet}</div>
        )}
        {creators.map((c) => (
          <div key={c.id} data-testid={`creator-row-${c.id}`} className="border border-white/10 bg-[#0C0C14] p-4 flex gap-3 items-start">
            <div className="w-14 h-14 shrink-0 border border-white/10 overflow-hidden bg-black/40 flex items-center justify-center">
              {c.avatar_url ? (
                <img src={c.avatar_url} alt="" className="w-full h-full object-cover" />
              ) : (
                <span className="text-white/30 font-mono text-[10px]">N/A</span>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-display font-black italic uppercase truncate">{c.name}</div>
              {c.role && <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-[#00E5FF]">{c.role}</div>}
              <div className="text-xs text-white/60 mt-1 line-clamp-2">{c.bio}</div>
              <div className="mt-2 flex gap-3 text-[10px] font-mono">
                <button onClick={() => setModal({
                  ...EMPTY,
                  ...c,
                  // Iter 29 — normalize null API values into empty strings so
                  // React's controlled <input> doesn't warn about a null value.
                  role: c.role ?? "",
                  bio: c.bio ?? "",
                  avatar_url: c.avatar_url ?? "",
                  discord_url: c.discord_url ?? "",
                  twitter_url: c.twitter_url ?? "",
                  youtube_url: c.youtube_url ?? "",
                  website_url: c.website_url ?? "",
                })} data-testid={`creator-row-${c.id}-edit`} className="text-white/70 hover:text-[#00E5FF]">{t.edit}</button>
                <button onClick={() => del(c.id)} data-testid={`creator-row-${c.id}-delete`} className="text-white/50 hover:text-[#FF0055]">{t.delete}</button>
                <span className="text-white/30 ml-auto">#{c.display_order ?? 0}</span>
              </div>
            </div>
          </div>
        ))}
      </div>

      {modal && (
        <Modal title={modal.id ? t.editCreator : t.newCreator} onClose={() => setModal(null)}>
          <div className="space-y-4">
            <SettingsField label={t.name} value={modal.name} onChange={(v) => setModal({ ...modal, name: v })} testid="creator-name" />
            <SettingsField label={t.creatorRole} value={modal.role} onChange={(v) => setModal({ ...modal, role: v })} placeholder="e.g. Founder & Judge" testid="creator-role" />
            <div>
              <label className="text-[10px] uppercase tracking-[0.25em] font-bold text-white/40 block mb-2">{t.bio}</label>
              <textarea
                value={modal.bio}
                onChange={(e) => setModal({ ...modal, bio: e.target.value })}
                rows={4}
                data-testid="creator-bio"
                className="w-full bg-transparent border border-white/15 px-3 py-2 text-sm focus:outline-none focus:border-[#00E5FF] font-mono"
              />
            </div>
            <SettingsField label={t.avatarUrl} value={modal.avatar_url} onChange={(v) => setModal({ ...modal, avatar_url: v })} placeholder="https://..." testid="creator-avatar" />
            <div className="grid grid-cols-2 gap-3">
              <SettingsField label="Discord" value={modal.discord_url} onChange={(v) => setModal({ ...modal, discord_url: v })} placeholder="https://discord.gg/..." testid="creator-discord" />
              <SettingsField label="Twitter/X" value={modal.twitter_url} onChange={(v) => setModal({ ...modal, twitter_url: v })} placeholder="https://x.com/..." testid="creator-twitter" />
              <SettingsField label="YouTube" value={modal.youtube_url} onChange={(v) => setModal({ ...modal, youtube_url: v })} placeholder="https://youtube.com/..." testid="creator-youtube" />
              <SettingsField label={t.website} value={modal.website_url} onChange={(v) => setModal({ ...modal, website_url: v })} placeholder="https://..." testid="creator-website" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.25em] font-bold text-white/40 block mb-2">{t.displayOrder}</label>
              <input
                type="number"
                value={modal.display_order}
                onChange={(e) => setModal({ ...modal, display_order: e.target.value })}
                data-testid="creator-order"
                className="w-full bg-transparent border border-white/15 px-3 py-2 text-sm focus:outline-none focus:border-[#00E5FF] font-mono"
              />
            </div>
            <div className="flex justify-end gap-3 pt-2 border-t border-white/10">
              <button onClick={() => setModal(null)} className="px-4 py-2 text-white/50 hover:text-white text-xs uppercase tracking-[0.2em] font-bold">{t.cancel}</button>
              <button onClick={save} disabled={busy} data-testid="creator-save" className="px-4 py-2 bg-[#FF0055] hover:bg-[#FF0055]/90 text-white text-xs uppercase tracking-[0.2em] font-bold disabled:opacity-50">
                {busy ? "…" : t.save}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
